import React, {
  PropsWithChildren,
  createContext,
  useState,
  useEffect,
} from 'react';

import Router from 'next/router';

type Props = PropsWithChildren<{}>;

type RouterHistoryEntity = {
  url: string;
  pathname?: string;
  isReferrer?: boolean;
};
type RouterHistory = RouterHistoryEntity[];

// context
export const RouterHistoryContext = createContext<RouterHistory>([]);

// utils
const getPathname = () => (Router.router ? Router.router.pathname : undefined);

// provider component
export const RouterHistoryProvider = ({ children }: Props) => {
  const [routerHistory, setRouterHistory] = useState<RouterHistory>([]);

  useEffect(() => {
    // router on change handler
    const onRouterChangeComplete = (url: string) => {
      const lastSavedUrl = routerHistory[routerHistory.length - 1];
      if (lastSavedUrl && url === lastSavedUrl.url) return;

      const newHistory = [...routerHistory, { url, pathname: getPathname() }];
      setRouterHistory(newHistory);
    };

    // mount router on change event
    Router.events.on('routeChangeComplete', onRouterChangeComplete);
    // un-mount router on change event
    return () =>
      Router.events.off('routeChangeComplete', onRouterChangeComplete);
  }, [routerHistory, setRouterHistory]);

  // initial route and referrer fill
  useEffect(() => {
    const initialRouterHistory: RouterHistory = [];
    // get document referrer
    if (document.referrer) {
      initialRouterHistory.push({
        url: document.referrer,
        isReferrer: true,
      });
    }
    // add current router history
    const { pathname, search } = window.location;
    const url = `${pathname}${search}`;
    initialRouterHistory.push({ url, pathname: getPathname() });

    setRouterHistory(initialRouterHistory);
  }, [setRouterHistory]);

  return (
    <RouterHistoryContext.Provider value={routerHistory}>
      {children}
    </RouterHistoryContext.Provider>
  );
};
